import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Modal from 'antd/lib/modal';
import Input from 'antd/lib/input';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, ToolOutlined } from '@ant-design/icons';
import { IssueEntity, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import { antGrey, getAvatarColorForName, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { selectUserById, selectUserCanPerformAction } from '@/selectors/selectors';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import {
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE_FIX_REQUEST,
    TriageViewType,
    tryTransitionIssue,
    tryUpdateIssueFixRequest,
} from '@/actions/triage';
import { useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import PermissionedActionButton, { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { fixRequestStatusMeta, LABEL_STYLE } from './TriageIssueCard';

interface FixRequestSectionProps {
    orgId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
}

export const FixRequestSection: React.FC<FixRequestSectionProps> = ({ orgId, issue, viewType }) => {
    const dispatch = useDispatch();
    const fixRequest = issue.fixRequest;
    const requester = useSelector((state: RootState) =>
        fixRequest?.requestedByUserId ?
            selectUserById(state, orgId, issue.appId, fixRequest.requestedByUserId)
        :   undefined,
    );
    const requesterName = requester ? displayUserNameLong(requester) : (fixRequest?.requestedByUserId ?? '');
    const canApproveReject = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateFixRequestStatus),
    );
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const isUpdatingFixRequest = useIsLoading(TRY_UPDATE_ISSUE_FIX_REQUEST, issue.id);
    const fixRequestUpdated = useUpdated(TRY_UPDATE_ISSUE_FIX_REQUEST, issue.id);

    const [editOpen, setEditOpen] = React.useState(false);
    const [editValue, setEditValue] = React.useState('');
    const [editSubmitted, setEditSubmitted] = React.useState(false);
    const openEdit = () => {
        setEditValue(fixRequest?.details ?? '');
        setEditSubmitted(false);
        setEditOpen(true);
    };
    const submitEdit = () => {
        setEditSubmitted(true);
        dispatch(tryUpdateIssueFixRequest({ orgId, appId: issue.appId, issueId: issue.id, details: editValue.trim() }));
    };
    React.useEffect(() => {
        if (editSubmitted && fixRequestUpdated) {
            setEditSubmitted(false);
            setEditOpen(false);
        }
    }, [editSubmitted, fixRequestUpdated]);

    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState, viewType }));

    if (!fixRequest) {
        return null;
    }

    return (
        <>
            <Collapse
                className="triage-collapse"
                ghost
                bordered={false}
                defaultActiveKey={['section']}
                items={[
                    {
                        key: 'section',
                        showArrow: false,
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <ToolOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Fix request</Text>
                            </div>
                        ),
                        extra: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {fixRequestStatusMeta(issue.state).icon}
                                <Text style={LABEL_STYLE}>{fixRequestStatusMeta(issue.state).label}</Text>
                            </div>
                        ),
                        children: (
                            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 16,
                                            color: fixRequest.details ? antGrey.grey8 : antGrey.grey6,
                                            fontStyle: fixRequest.details ? undefined : 'italic',
                                        }}
                                    >
                                        {fixRequest.details || 'Add details'}
                                    </Text>
                                    <PermissionedActionButton
                                        type="link"
                                        size="small"
                                        actionType={RunwayUserAction.UAManageTriageIssues}
                                        icon={<EditOutlined />}
                                        onClick={openEdit}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: 24 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Target version</Text>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                height: 32,
                                                padding: '0 11px',
                                                width: 'fit-content',
                                                minWidth: 104,
                                                border: `1px solid ${antGrey.grey5}`,
                                                borderRadius: 6,
                                                background: antGrey.grey2,
                                            }}
                                        >
                                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>
                                                {fixRequest.fixVersion ?? '--'}
                                            </Text>
                                        </div>
                                    </div>
                                    {requesterName && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Requested by</Text>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: 32,
                                                    padding: '3px 4px',
                                                    width: 'fit-content',
                                                    border: `1px solid ${antGrey.grey5}`,
                                                    borderRadius: 6,
                                                    background: antGrey.grey2,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '1px 8px',
                                                        borderRadius: 4,
                                                        background: antGrey.grey3,
                                                    }}
                                                >
                                                    <Avatar
                                                        size={20}
                                                        src={requester?.commsAvatars?.image32}
                                                        style={{
                                                            backgroundColor: getAvatarColorForName(requesterName),
                                                            fontSize: 11,
                                                            border: 'none',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {requesterName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Text style={{ color: antGrey.grey8, fontSize: 14 }}>
                                                        {requesterName}
                                                    </Text>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {issue.state === IssueState.FixRequested && (
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <Tooltip title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                            <Button
                                                type="primary"
                                                icon={<CheckCircleOutlined />}
                                                loading={isTransitioning}
                                                disabled={!canApproveReject}
                                                onClick={() => transition(IssueState.FixApproved)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor:
                                                        canApproveReject ? rwColors.statusGreen : undefined,
                                                    borderColor: canApproveReject ? rwColors.statusGreen : undefined,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Approve fix request
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                            <Button
                                                icon={<CloseCircleOutlined />}
                                                loading={isTransitioning}
                                                disabled={!canApproveReject}
                                                onClick={() => transition(IssueState.FixRejected)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor: canApproveReject ? rwColors.statusRed : undefined,
                                                    borderColor: canApproveReject ? rwColors.statusRed : undefined,
                                                    color: canApproveReject ? runwayColors.white : undefined,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Reject fix request
                                            </Button>
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
            <Modal
                title="Edit details"
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                onOk={submitEdit}
                okText="Save"
                okButtonProps={{ disabled: !editValue.trim() }}
                confirmLoading={isUpdatingFixRequest}
                destroyOnClose
                width={560}
            >
                <Input.TextArea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    autoFocus
                />
            </Modal>
        </>
    );
};
